/**
 * same-scenario-concurrent-play (Phase 5+ Tier 5E 第 11 段) Phase 4:
 * 409 受領後の reader 選択 dialog component。
 *
 * 4 軸採択 A2/B2/G2/D1: warning + 確認 dialog で reader に既存進行中 playline を
 * 認知させ、 3 resolution を選択させる。
 *
 * 3 resolution button + cancel button + 既存 playline meta (lastPlayedAt) 表示。
 *
 * 配置: standalone reusable component (= placement-agnostic、 親 surface が 409 受領時に
 * mount + onResolve handler で POST /playlines/conflict-resolve)。 base scope では
 * 本 component 自体が API call を行わず、 親 component に resolution + meta を渡す。
 *
 * a11y: role=dialog + aria-modal + Escape close + focus trap + initial focus (= sibling
 * SettingsPanel pattern 踏襲)。
 */
import { useCallback, useRef, useState, type JSX } from "react";
import type { ConcurrentPlayResolution } from "../../api/concurrent-play";
import { formatIsoToLocale } from "../../utils/formatIsoDate";
import { useDialogKeyboard } from "../../components/ui/useDialogKeyboard";

export interface ConcurrentPlayDialogProps {
  existingPlaylineId: string;
  existingLastPlayedAt: string | null;
  scenarioTitle: string | null;
  onResolve: (resolution: ConcurrentPlayResolution) => Promise<void> | void;
  onCancel: () => void;
}

function formatLastPlayed(iso: string | null): string {
  if (iso === null) return "前回プレイ日時 不明";
  const formatted = formatIsoToLocale(iso, "ja-JP");
  return formatted === null ? "前回プレイ日時 不明" : `前回プレイ: ${formatted}`;
}

export function ConcurrentPlayDialog({
  existingLastPlayedAt,
  scenarioTitle,
  onResolve,
  onCancel,
}: ConcurrentPlayDialogProps): JSX.Element {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [busy, setBusy] = useState<ConcurrentPlayResolution | null>(null);
  const [error, setError] = useState<string | null>(null);

  // useDialogKeyboard (= Escape close + initial focus + Tab focus trap)。
  // busy 中 (= resolve API 実行中) は Escape close を抑制する guard が必要。
  const handleEscapeCancel = useCallback(() => {
    if (busy === null) onCancel();
  }, [busy, onCancel]);
  useDialogKeyboard({ containerRef: dialogRef, onClose: handleEscapeCancel });

  async function handleResolve(
    resolution: ConcurrentPlayResolution,
  ): Promise<void> {
    if (busy !== null) return;
    setBusy(resolution);
    setError(null);
    try {
      await onResolve(resolution);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "選択の実行中にエラーが発生しました",
      );
      setBusy(null);
    }
  }

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="concurrent-play-dialog-title"
      data-testid="concurrent-play-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-reader-overlay p-4"
    >
      <div className="w-full max-w-md rounded-lg bg-reader-page shadow-xl">
        <div className="border-b border-reader-border p-4">
          <h2
            id="concurrent-play-dialog-title"
            className="text-lg font-semibold text-reader-primary"
          >
            進行中の物語があります
          </h2>
        </div>

        <div className="space-y-4 p-4">
          <p className="text-sm text-reader-secondary">
            このシナリオはすでに別の playline で進行中です。 続行方法を選択してください。
          </p>
          {scenarioTitle !== null ? (
            <p
              className="text-sm font-medium text-reader-primary"
              data-testid="concurrent-play-dialog-scenario-title"
            >
              {scenarioTitle}
            </p>
          ) : null}
          <p
            className="text-xs text-reader-muted"
            data-testid="concurrent-play-dialog-last-played"
          >
            {formatLastPlayed(existingLastPlayedAt)}
          </p>

          {error !== null && (
            <div
              role="alert"
              data-testid="concurrent-play-dialog-error"
              className="rounded bg-red-900/40 p-2 text-sm text-red-200"
            >
              {error}
            </div>
          )}

          <div className="space-y-2">
            <button
              type="button"
              data-testid="concurrent-play-dialog-resume"
              onClick={() => handleResolve("resume")}
              disabled={busy !== null}
              className="w-full rounded bg-reader-brand px-4 py-2 text-sm font-medium text-white disabled:opacity-50 hover:bg-reader-brand-hover"
            >
              {busy === "resume" ? "再開中..." : "前回の続きから再開"}
            </button>
            <button
              type="button"
              data-testid="concurrent-play-dialog-archive"
              onClick={() => handleResolve("archive-and-start-new")}
              disabled={busy !== null}
              className="w-full rounded border border-reader-border-strong bg-reader-surface px-4 py-2 text-sm text-reader-secondary disabled:opacity-50"
            >
              {busy === "archive-and-start-new"
                ? "保存して新規開始中..."
                : "前回を保存して新しく始める"}
            </button>
            <button
              type="button"
              data-testid="concurrent-play-dialog-pause"
              onClick={() => handleResolve("pause-and-start-new")}
              disabled={busy !== null}
              className="w-full rounded border border-reader-border-strong bg-reader-surface px-4 py-2 text-sm text-reader-secondary disabled:opacity-50"
            >
              {busy === "pause-and-start-new"
                ? "一時停止して新規開始中..."
                : "前回を一時停止して新しく始める"}
            </button>
          </div>
        </div>

        <div className="flex justify-end border-t border-reader-border p-4">
          <button
            type="button"
            data-testid="concurrent-play-dialog-cancel"
            onClick={onCancel}
            disabled={busy !== null}
            className="rounded border border-reader-border-strong px-4 py-2 text-sm text-reader-secondary disabled:opacity-50"
          >
            キャンセル
          </button>
        </div>
      </div>
    </div>
  );
}
